var __jsx = React.createElement;
import { FullscreenExitOutlined, FullscreenOutlined, LeftOutlined, PlayCircleFilled, RightOutlined } from '@ant-design/icons';
import classNames from 'classnames';
import DixuSpin from "../DixuSpin/DixuSpin";
import DixuText from "../DixuText/DixuText";
import DixuVimeoVideo from "../DixuVimeoVideo/DixuVimeoVideo";
import DixuYoutubeVideo from "../DixuYoutubeVideo/DixuYoutubeVideo";
import useDragToScroll from "../../hooks/useDragToScroll";
import usePreventBodyScroll from "../../hooks/usePreventBodyScroll";
import useRestrictFocus from "../../hooks/useRestrictFocus";
import React, { useEffect, useRef, useState } from 'react';
import { Portal } from 'react-portal';
import { loadImages } from "../../utils/Utils";
import DixuImage from "../DixuImage/DixuImage";
import { ConditionalField } from "../../utils/ComponentUtils";
const DixuCarouselNavItem = _ref => {
  let {
    label,
    src,
    thumbnailSrc,
    onClick,
    active,
    sticky,
    overlay,
    children
  } = _ref;
  const elRef = useRef();
  const classes = classNames('dixu-carousel__nav-item', {
    'dixu-carousel__nav-item--active': active,
    'dixu-carousel__nav-item--sticky': sticky
  });
  useEffect(() => {
    if (active) {
      setTimeout(() => {
        const {
          current: element
        } = elRef;
        if (!element) {
          return;
        }
        const {
          offsetLeft,
          parentNode
        } = element;
        const {
          width
        } = element.getBoundingClientRect();
        if (parentNode) {
          parentNode.scrollLeft = offsetLeft - 2 * width;
        }
      });
    }
  }, [active]);
  return /*#__PURE__*/React.createElement("li", {
    ref: elRef,
    className: classes
  }, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dixu-carousel__nav-button",
    onClick: onClick,
    "aria-label": label
  }, children, !children && /*#__PURE__*/React.createElement(DixuImage, {
    src: thumbnailSrc || src,
    plain: true
  })), overlay && /*#__PURE__*/React.createElement("div", {
    className: "dixu-carousel__nav-button-overlay"
  }, overlay));
};
const ImageDescription = _ref2 => {
  let {
    description
  } = _ref2;
  return /*#__PURE__*/React.createElement("div", {
    className: "dixu-carousel__image-description-wrapper"
  }, /*#__PURE__*/React.createElement(DixuText, {
    font: 2,
    weight: "bold",
    color: "2-1"
  }, description));
};
const DixuCarousel = _ref3 => {
  let {
    slides,
    allowFullscreen,
    aspectRatio,
    preloadImageAmount,
    portal,
    className
  } = _ref3;
  const {
    0: activeSlideIndex,
    1: setActiveSlideIndex
  } = useState(() => slides.findIndex(slide => {
    const {
      src,
      thumbnailSrc
    } = slide;
    return src || thumbnailSrc;
  }));
  const {
    0: prevActiveSlideIndex,
    1: setPrevActiveSlideIndex
  } = useState(activeSlideIndex);
  const {
    0: isFullscreen,
    1: setIsFullscreen
  } = useState(false);
  const {
    0: loadingImages,
    1: setLoadingImages
  } = useState(true);
  const carouselElRef = useRef();
  const prevSlideRef = useRef();
  usePreventBodyScroll(isFullscreen);
  useRestrictFocus({
    condition: isFullscreen,
    getHostElement: () => carouselElRef.current
  });
  const activeSlide = slides[activeSlideIndex] || {};
  const prevSlide = slides[prevActiveSlideIndex] || {};
  const activeSlideIsVideo = !!activeSlide.videoProvider;
  const prevSlideIsVideo = !!prevSlide.videoProvider;
  const classes = classNames('dixu-carousel', className, {
    'border-radius-lg': !isFullscreen,
    'dixu-carousel--fullscreen': isFullscreen,
    'dixu-carousel--video-active': activeSlideIsVideo,
    'dixu-carousel--no-nav': slides.length < 2
  });
  const clampSlideIndex = index => {
    const slideCount = slides.length;
    if (index > slideCount - 1) {
      return clampSlideIndex(index - slideCount);
    }
    if (index < 0) {
      return clampSlideIndex(slideCount + index);
    }
    return index;
  };
  const goToSlide = index => {
    const clampedIndex = clampSlideIndex(index);
    if (clampedIndex !== activeSlideIndex) {
      const nextSlide = slides[clampedIndex];
      const {
        src,
        thumbnailSrc
      } = nextSlide;
      if (!src && !thumbnailSrc) {
        return false;
      }
      setPrevActiveSlideIndex(activeSlideIndex);
      setActiveSlideIndex(clampedIndex);
      loadImages([slides?.[clampedIndex - 1], slides?.[clampedIndex + 1]].filter(Boolean).flatMap(slide => {
        const {
          src: slideSrc,
          thumbnailSrc: slideThumbnailSrc,
          videoProvider
        } = slide;
        return videoProvider ? null : [slideSrc, slideThumbnailSrc];
      }).filter(Boolean));
      const prevSlideEl = prevSlideRef.current;
      if (prevSlideRef) {
        // https://stackoverflow.com/a/45036752
        prevSlideEl.style.animation = 'none';
        prevSlideEl.offsetHeight;
        prevSlideEl.style.animation = '';
      }
    }
    return true;
  };
  const goToNextSlide = () => {
    const maxAttempts = slides.length;
    let attempts = 0;
    const action = () => goToSlide(activeSlideIndex + (1 + attempts));
    while (attempts < maxAttempts && !action()) {
      attempts += 1;
      action();
    }
  };
  const goToPrevSlide = () => {
    const maxAttempts = slides.length;
    let attempts = 0;
    const action = () => goToSlide(activeSlideIndex - (1 + attempts));
    while (attempts < maxAttempts && !action()) {
      attempts += 1;
      action();
    }
  };
  const toggleFullscreen = () => {
    if (allowFullscreen) {
      setIsFullscreen(!isFullscreen);
    }
  };
  const handleKeyboardNavigation = event => {
    const {
      key
    } = event;
    if (key === 'ArrowLeft') {
      event.preventDefault();
      goToPrevSlide();
    } else if (key === 'ArrowRight') {
      event.preventDefault();
      goToNextSlide();
    } else if (key === ' ') {
      event.preventDefault();
      goToNextSlide();
    } else if (key === 'Enter') {
      event.preventDefault();
      toggleFullscreen();
    } else if (key === 'Escape') {
      event.preventDefault();
      setIsFullscreen(false);
    }
  };
  const navItems = slides.map((slide, index) => {
    const {
      label = index + 1,
      src,
      thumbnailSrc,
      onClick,
      thumbnailContent,
      stickyThumbnail,
      videoProvider
    } = slide;
    return /*#__PURE__*/React.createElement(DixuCarouselNavItem, {
      key: index.toString(36),
      label: label,
      src: src,
      thumbnailSrc: thumbnailSrc,
      active: index === activeSlideIndex,
      sticky: stickyThumbnail,
      overlay: !!videoProvider && /*#__PURE__*/React.createElement(DixuText, {
        font: "8",
        color: "2-1",
        dropShadow: "2",
        style: {
          opacity: 0.75
        }
      }, /*#__PURE__*/React.createElement(PlayCircleFilled, null)),
      onClick: onClick ? () => onClick({
        goToSlide: () => goToSlide(index)
      }) : () => goToSlide(index)
    }, thumbnailContent);
  });
  const renderedActiveSlide = (() => {
    const {
      src,
      videoID,
      videoProvider,
      thumbnailSrc,
      description
    } = activeSlide;
    if (activeSlideIsVideo) {
      if (videoProvider === 'YouTube') {
        return /*#__PURE__*/React.createElement(DixuYoutubeVideo, {
          src: src,
          videoID: videoID,
          aspectRatio: aspectRatio,
          thumbnail: thumbnailSrc,
          plain: isFullscreen
        });
      }
      if (videoProvider === 'Vimeo') {
        return /*#__PURE__*/React.createElement(DixuVimeoVideo, {
          src: src,
          videoID: videoID,
          aspectRatio: aspectRatio,
          thumbnail: thumbnailSrc,
          plain: isFullscreen
        });
      }
    }
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(DixuImage, {
      aspectRatio: aspectRatio,
      src: src,
      plain: isFullscreen,
      fit: "contain"
    }), /*#__PURE__*/React.createElement(ConditionalField, {
      condition: description
    }, /*#__PURE__*/React.createElement(ImageDescription, {
      description: description
    })));
  })();
  const renderedPrevSlide = (() => {
    const {
      src
    } = prevSlide;
    if (prevSlideIsVideo) {
      return null;
    }
    return /*#__PURE__*/React.createElement(DixuImage, {
      aspectRatio: aspectRatio,
      src: src,
      plain: isFullscreen,
      fit: "contain"
    });
  })();
  useEffect(() => {
    let isMounted = true;
    loadImages(slides?.slice(0, preloadImageAmount).flatMap(slide => {
      const {
        src,
        thumbnailSrc,
        videoProvider
      } = slide;
      return videoProvider ? null : [src, thumbnailSrc];
    }).filter(Boolean)).then(() => {
      if (isMounted) {
        setLoadingImages(false);
      }
    });
    return () => {
      isMounted = false;
    };
  }, [slides]);
  const navItemsRef = useRef();
  useDragToScroll(() => navItemsRef.current);
  const carousel = /*#__PURE__*/React.createElement(DixuSpin, {
    spinning: loadingImages,
    fullOpacity: true
  }, /*#__PURE__*/React.createElement("div", {
    ref: carouselElRef,
    className: classes,
    tabIndex: "0",
    onKeyDown: handleKeyboardNavigation
  }, /*#__PURE__*/React.createElement("div", {
    className: "dixu-carousel__slide-container",
    onDoubleClick: toggleFullscreen
  }, /*#__PURE__*/React.createElement("div", {
    className: "dixu-carousel__slide"
  }, renderedActiveSlide), /*#__PURE__*/React.createElement("div", {
    ref: prevSlideRef,
    className: "dixu-carousel__slide dixu-carousel__slide--prev"
  }, renderedPrevSlide), /*#__PURE__*/React.createElement("div", {
    className: "dixu-carousel__controls-container"
  }, slides.length > 1 && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dixu-carousel__control dixu-carousel__control--left",
    onClick: goToPrevSlide,
    onDoubleClick: event => event.stopPropagation(),
    "aria-label": "Previous"
  }, /*#__PURE__*/React.createElement(LeftOutlined, null)), /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dixu-carousel__control dixu-carousel__control--right",
    onClick: goToNextSlide,
    onDoubleClick: event => event.stopPropagation(),
    "aria-label": "Next"
  }, /*#__PURE__*/React.createElement(RightOutlined, null))), allowFullscreen && /*#__PURE__*/React.createElement("button", {
    type: "button",
    className: "dixu-carousel__control dixu-carousel__control--bottom-right",
    onClick: toggleFullscreen,
    onDoubleClick: event => event.stopPropagation(),
    "aria-label": "Toggle fullscreen"
  }, isFullscreen ? /*#__PURE__*/React.createElement(FullscreenExitOutlined, null) : /*#__PURE__*/React.createElement(FullscreenOutlined, null)))), navItems.length > 1 && /*#__PURE__*/React.createElement("nav", {
    className: "dixu-carousel__nav",
    "aria-label": "Carousel"
  }, /*#__PURE__*/React.createElement("ol", {
    ref: navItemsRef,
    className: "dixu-carousel__nav-items"
  }, navItems))));
  if (portal && isFullscreen) {
    return /*#__PURE__*/React.createElement(Portal, null, carousel);
  }
  return carousel;
};
DixuCarousel.defaultProps = {
  slides: [],
  allowFullscreen: true,
  aspectRatio: '3:2',
  preloadImageAmount: 5,
  portal: true
};
export default DixuCarousel;